import { EyeOff, Image, TrendingUp } from 'react-feather';
import { ReactNode, useContext } from 'react';
import { useModal } from 'react-modal-hook';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { CheckoutStep } from '../Checkout/helpers';
import { EmbedContext } from './EmbedProvider';
import { EventInfo } from '../../common/Common/EventHeader';
import { resizedImageUrl } from '../../common/helpers';
import { useEvent } from './EventProvider';
import AtletaLogo from '../../common/Common/AtletaLogo';
import CookieConsentBar from '../../common/Common/CookieConsentBar';
import LanguageSelector from '../../common/Common/LanguageSelector';
import PopularEventLabel from '../../common/PopularEventLabel';
import UI from '../../common/UI';
import config from '../config';
import useLocale from '../../common/useLocale';

interface LayoutProps {
  focusMode?: boolean;
  children?: ReactNode;
}

const Layout = ({ focusMode, children }: LayoutProps) => {
  const { t } = useTranslation();
  const { embedded } = useContext(EmbedContext);
  const { event } = useEvent();

  return (
    <>
      <Container id="ContentContainer">
        {!event.active && (
          <UI.Card sc={{ background: 'tertiary.600', color: 'tertiary.100', spacing: 'sm' }}>
            <UI.H5 sc={{ color: 'white', strong: true }}>
              <UI.Icon>
                <EyeOff />
              </UI.Icon>
              {' '}
              {t('layout.event_inactive')}
            </UI.H5>
            {t('layout.event_inactive_description')}
          </UI.Card>
        )}

        {!focusMode && <EventHeader />}

        {!focusMode && !embedded && event.trend_score >= 60 && (
          <UI.Div sc={{ small: true }}>
            <PopularEventLabel>
              <UI.Icon>
                <TrendingUp />
              </UI.Icon>
              {' '}
              {t('event_info.popular')}
            </PopularEventLabel>
          </UI.Div>
        )}

        <EventTitle />

        {!focusMode && <NavBar />}

        {children}
      </Container>

      <Footer />

      {!embedded && <CookieConsentBar />}
    </>
  );
};

export const SectionTitle = styled(UI.H3)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 400;
  `}
`;

export const Container = styled(UI.Div)<{ size?: 'sm' | 'md' | 'lg'; }>`
  ${({ size = 'sm', theme }) => css`
    min-width: 320px;
    max-width: ${size === 'sm' ? `${theme.breakpoints.md}px` : (size === 'md' ? `${theme.breakpoints.lg}px` : `${theme.breakpoints.xl}px`)};
    width: 100%;
    margin: 0 auto;
    display: grid;

   ${size === 'sm' && css`
      padding: ${theme.gutter * 1.25}px ${theme.gutter * (2 / 3)}px;
      grid-gap: ${theme.gutter * 1.25}px;
    `}

    ${size !== 'sm' && css`
      padding: ${theme.gutter * 1.5}px ${theme.gutter * (2 / 3)}px;
      grid-gap: ${theme.gutter * 1.5}px;
    `}

    @media ${theme.devices.md} {
      ${size === 'sm' && css`
        padding: ${theme.gutter * 1.5}px;
      `}

      ${size !== 'sm' && css`
        padding: ${theme.gutter * 2}px;
      `}
    }

    > ${UI.HR} {
      margin: 0;
    }
  `}
`;

export const NavBar = () => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const { event } = useEvent();

  const match = useRouteMatch<{ path?: string; }>('/:eventId/:path');

  const registrationTabIsActive = [
    'preregistration',
    'waitlist',
    ...Object.values(CheckoutStep),
  ].includes(match?.params.path);

  return (
    <UI.Div sc={{ mx: [-8 / 3, 0] }} style={{ overflow: 'hidden' }}>
      <UI.Tabs sc={{ border: true, px: [3, 0], mt: -2 }}>
        <UI.Tab
          to={`/${event.id}`}
          exact
          resetScrollPosition={false}
        >
          {t('layout.event')}
        </UI.Tab>
        <UI.Tab
          to={`/${event.id}/tickets`}
          isActive={() => registrationTabIsActive}
          resetScrollPosition={false}
        >
          {t('layout.tickets')}
        </UI.Tab>
        {event.enable_public_resale && (
          <UI.Tab
            to={`/${event.id}/resale`}
            resetScrollPosition={false}
          >
            {t('resale.resale')}
            {event.cached_registrations_for_sale_count > 0 && (
              <>
                {' '}
                <UI.Badge sc={{ small: true, my: -0.25 }}>
                  {formatNumber(event.cached_registrations_for_sale_count)}
                </UI.Badge>
              </>
            )}
          </UI.Tab>
        )}
        {event.enable_interest_list && (
          <UI.Tab
            to={`/${event.id}/interest`}
            resetScrollPosition={false}
          >
            {t('interest_list.newsletter')}
          </UI.Tab>
        )}
      </UI.Tabs>
    </UI.Div>
  );
};

export const EventHeader = () => {
  const { t } = useTranslation();
  const { embedded } = useContext(EmbedContext);
  const { formatNumber } = useLocale();
  const { event } = useEvent();

  const [showPhotosModal, closePhotosModal] = useModal(() => (
    <UI.Modal maxWidth={900} close={closePhotosModal} onClick={closePhotosModal}>
      <UI.GridContainer sc={{ gutter: [1, 1.25] }}>
        <UI.H3>
          {event.title}
        </UI.H3>
        {event.photos.map((photo) => (
          <img
            src={resizedImageUrl(photo.file.url, 1200)}
            style={{ width: '100%', display: 'block', borderRadius: 8, background: 'rgba(0, 0, 0, 0.1)' }}
            alt={event.title}
            key={photo.id}
          />
        ))}
      </UI.GridContainer>
    </UI.Modal>
  ), []);

  return (
    <>
      {event.photos.length > 0 && (
        <>
          <UI.Div
            sc={{
              borderRadius: ['none', embedded ? 'none' : '2xl'],
              mx: [-8 / 3, embedded ? -6 : -6],
              mt: [-5, embedded ? -6 : 0],
              relative: true,
            }}
            style={{
              aspectRatio: 2.25,
              overflow: 'hidden',
            }}
            id="Header"
          >
            {event.photos.length > 1 && (
              <UI.Div
                sc={{ absolute: true, pr: 3, pb: 3 }}
                style={{
                  bottom: 0, right: 0, zIndex: 1,
                }}
              >
                <UI.Button
                  sc={{ brand: 'gray.50/95', size: 'sm', small: true }}
                  onClick={() => showPhotosModal()}
                >
                  <UI.Icon>
                    <Image />
                  </UI.Icon>
                  {' '}
                  {t('event_info.view_all')}
                  {' ('}
                  {formatNumber(event.photos.length)}
                  )
                </UI.Button>
              </UI.Div>
            )}
            {event.photos.length >= 3 && (
              <UI.GridContainer sc={{ columns: '1fr 1fr 1fr', gutter: 0.125 }} style={{ height: '100%' }}>
                <UI.Div
                  style={{
                    gridColumn: '1 / span 2',
                    gridRow: '1 / span 2',
                    overflow: 'hidden',
                    background: 'rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <EventHeaderPhoto
                    src={resizedImageUrl(event.photos[0].file.url, 900)}
                    alt={event.title}
                    onClick={() => showPhotosModal()}
                  />
                </UI.Div>
                <UI.Div style={{ gridColumn: '3', gridRow: '1', overflow: 'hidden', background: 'rgba(0, 0, 0, 0.1)' }}>
                  <EventHeaderPhoto
                    src={resizedImageUrl(event.photos[1].file.url, 900)}
                    alt={event.title}
                    onClick={() => showPhotosModal()}
                  />
                </UI.Div>
                <UI.Div style={{ gridColumn: '3', gridRow: '2', overflow: 'hidden', background: 'rgba(0, 0, 0, 0.1)' }}>
                  <EventHeaderPhoto
                    src={resizedImageUrl(event.photos[2].file.url, 900)}
                    alt={event.title}
                    onClick={() => showPhotosModal()}
                  />
                </UI.Div>
              </UI.GridContainer>
            )}
            {event.photos.length > 0 && event.photos.length < 3 && (
              <UI.Div style={{ background: 'rgba(0, 0, 0, 0.1)', height: '100%' }}>
                <EventHeaderPhoto
                  src={resizedImageUrl(event.photos[0].file.url, 1200)}
                  alt={event.title}
                  onClick={() => showPhotosModal()}
                />
              </UI.Div>
            )}
          </UI.Div>
        </>
      )}
    </>
  );
};

export const EventTitle = () => {
  const { event } = useEvent();
  const { embedded } = useContext(EmbedContext);

  return (
    <UI.GridContainer
      sc={{
        columns: event.logo ? ['48px 1fr', '60px 1fr'] : '1fr',
        gutter: 0.5,
        alignVertical: 'center',
      }}
    >
      {event.logo && (
        <UI.Div>
          <UI.A href={!embedded ? `${event.checkout_url}` : undefined}>
            <UI.ProfilePic
              sc={{ background: 'white', size: [48, 60] }}
            >
              <img src={resizedImageUrl(event.logo.url, 300, 'webp')} alt={event.title} />
            </UI.ProfilePic>
          </UI.A>
        </UI.Div>
      )}
      <UI.Div sc={{ fontWeight: 500 }}>
        <UI.H1 sc={{ fontSize: [3, 4], mb: 0.5 }} style={{ fontWeight: 700 }}>
          <UI.A href={!embedded ? `${event.checkout_url}` : undefined} sc={{ color: 'gray.800' }}>
            {event.title}
          </UI.A>
        </UI.H1>
        <EventInfo event={event} />
      </UI.Div>
    </UI.GridContainer>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  const { embedded } = useContext(EmbedContext);
  const { locale } = useLocale();
  const { event } = useEvent();

  return (
    <>
      <UI.HR sc={{ my: 0 }} />
      <Container size={!embedded ? 'lg' : 'sm'}>
        <UI.FlexContainer
          sc={{
            justifyContent: [undefined, 'space-between'],
            alignItems: 'center',
            direction: ['column', 'row'],
          }}
        >
          <UI.FlexContainer
            sc={{
              spaceX: [0, 4],
              spaceY: [1, 0],
              justifyContent: ['start', 'start'],
              alignItems: 'center',
              direction: ['column', 'row'],
              small: true,
            }}
          >
            <UI.A
              href={config.baseUrl}
              target={embedded ? '_blank' : undefined}
              rel="noopener noreferrer"
              sc={{ mt: 0.5, mb: -0.5 }}
            >
              <AtletaLogo symbolOnly symbolColor="gray.500" height={14} />
            </UI.A>
            {!embedded && (
              <>
                <UI.A
                  href={`/${locale}/calendar`}
                  sc={{ secondary: true, noWrap: true }}
                >
                  {t('footer.event_calendar')}
                </UI.A>
              </>
            )}
            <UI.A
              href="/me"
              sc={{ secondary: true, noWrap: true }}
              target={embedded ? '_blank' : undefined}
              rel="noopener noreferrer"
            >
              {t('footer.account')}
            </UI.A>
            {!embedded && (
              <>
                <UI.A
                  href={`https://about.atleta.cc/${locale}/participant-support`}
                  sc={{ secondary: true, noWrap: true }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.help')}
                </UI.A>
                <UI.A
                  href={`https://about.atleta.cc/${locale}`}
                  sc={{ secondary: true, noWrap: true }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.atleta_for_organisers')}
                </UI.A>
                <UI.A
                  href={`https://about.atleta.cc/${locale}/terms-and-conditions`}
                  sc={{ secondary: true, noWrap: true }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.terms')}
                </UI.A>
              </>
            )}
            <UI.Link
              to={`/${event.id}/privacy`}
              sc={{ secondary: true, noWrap: true }}
              target={embedded ? '_blank' : undefined}
              rel="noopener noreferrer"
            >
              {t('common:privacy_statement.privacy_statement')}
            </UI.Link>
          </UI.FlexContainer>
          <UI.Div sc={{ mt: [3, 0] }}>
            <LanguageSelector blank />
          </UI.Div>
        </UI.FlexContainer>
      </Container>
    </>
  );
};

export const TitleBar = styled(UI.Div)`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 90px 1fr 90px;
    background: ${theme.getColor('secondary.300/25')};
    color: ${theme.mix(theme.colors.secondary[300], theme.colors.gray[400], 0.5)};
    margin-bottom: -15px;
    z-index: -1;
    height: 60px;
    align-items: center;
    padding-bottom: 15px;
    border-top-left-radius: ${theme.borderRadiuses.xl}px;
    border-top-right-radius: ${theme.borderRadiuses.xl}px;

    // Back button
    > ${UI.Div}:first-child {
      padding-left: ${theme.gutter * 0.75}px;

      a {
        height: 45px;
        transition: all 0.15s ease-in-out;
        display: inline-flex;
        align-items: center;

        ${UI.Icon} {
          border-radius: 100%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${theme.transparentize(theme.colors.secondary[500], 0.3)};
          color: ${theme.getTitleColor(theme.colors.secondary[500])};
          transition: all 0.15s ease-in-out;
          margin: 0;
          font-size: 1rem;
        }

        &:hover {
          ${UI.Icon} {
            background: ${theme.colors.secondary[500]};
          }
        }
      }
    }

    // Step title
    > ${UI.Div}:nth-child(2) {
      color: ${theme.mix(theme.colors.secondary[500], theme.colors.gray[800], 0.8)};
      text-align: center;
      font-weight: 500;
    }

    // Step number
    > ${UI.Div}:nth-child(3) {
      color: ${theme.mix(theme.colors.secondary[500], theme.colors.gray[800], 0.8)};
      text-align: right;
      padding: 0 ${theme.gutter * 0.75}px;
      font-size: ${12 / 16}rem;
      font-weight: 400;
    }

    @media ${theme.devices.md} {
      // Back button
      > ${UI.Div}:first-child {
        padding-left: ${theme.gutter}px;
      }

      // Step number
      > ${UI.Div}:nth-child(3) {
        padding-right: ${theme.gutter}px;
      }
    }
  `}
`;

const EventHeaderPhoto = styled.img`
  ${({ onClick }) => css`
    object-fit: cover;
    object-position: center center;
    width: 100%;
    height: 100%;

    ${onClick && css`
      cursor: pointer;
    `}
  `}
`;

export default Layout;
